import { useTranslation } from 'react-i18next';
import { ModuleIdSchema, SUPPORTED_LOCALES } from '@prody/shared';
import {
  Checkbox, Code, Field, Input, ListPane, PageHeader, Section, Select,
  Stamp, Workbench,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import './settings.css';

/* ==================================================================
   Business settings.

   This is the screen that makes Prody a platform rather than one
   company's admin. Currency, timezone, formatting locale, tax scheme,
   when the trading day starts and which modules exist all live here,
   per business — nothing about them is compiled in.

   The module switches are the honest expression of the registry: turn
   one off and the navigation, the routes, the command palette and the
   global search all lose it, with no other change anywhere.
   ================================================================== */

const TIMEZONES = [
  'Europe/Madrid', 'Europe/Lisbon', 'Europe/Paris', 'Europe/Berlin',
  'Europe/Moscow', 'Asia/Almaty', 'America/New_York', 'UTC',
];

const FORMAT_LOCALES = ['es-ES', 'ca-ES', 'en-GB', 'en-US', 'ru-RU', 'de-DE', 'fr-FR'];
const CURRENCIES = ['EUR', 'USD', 'GBP', 'RUB', 'KZT'];

export default function SettingsPage() {
  const { t } = useTranslation(['settings', 'common']);
  const { business, format, role } = useBusiness();
  const settings = business.settings;
  const readOnly = role !== 'owner';

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={<Code>{business.slug}</Code>}
      />

      <ListPane>
        <div className="pd-settings">
          <Section title={t('general')}>
            <div className="pd-settings__grid">
              <Field label={t('common:labels.customer')}>
                {({ id }) => <Input id={id} defaultValue={business.name} disabled={readOnly} />}
              </Field>
              <Field label={t('common:labels.sku')}>
                {({ id }) => <Input id={id} defaultValue={business.taxId ?? ''} disabled={readOnly} />}
              </Field>
              <Field label={t('common:labels.none')} className="pd-settings__span">
                {({ id }) => <Input id={id} defaultValue={business.legalName} disabled={readOnly} />}
              </Field>
            </div>
          </Section>

          <Section title={t('localisation')}>
            <div className="pd-settings__grid">
              <Field label={t('currency')}>
                {({ id }) => (
                  <Select id={id} defaultValue={settings.currency} disabled={readOnly}>
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('timezone')}>
                {({ id }) => (
                  <Select id={id} defaultValue={settings.timezone} disabled={readOnly}>
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field
                label={t('formatLocale')}
                /* Deliberately separate from the UI language: a Russian
                   speaker running a Barcelona business wants the
                   interface in Russian and the numbers as 1.234,56 €. */
                description={format.money({ minor: 123456, currency: settings.currency })}
              >
                {({ id }) => (
                  <Select id={id} defaultValue={settings.formatLocale} disabled={readOnly}>
                    {FORMAT_LOCALES.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('common:appearance.language')}>
                {({ id }) => (
                  <Select id={id} defaultValue={settings.defaultLocale} disabled={readOnly}>
                    {SUPPORTED_LOCALES.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('dayStartsAt')} description={t('dayStartsAtHint')}>
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="time"
                    defaultValue={settings.dayStartsAt}
                    disabled={readOnly}
                  />
                )}
              </Field>

              <Field label={t('orderCutoff')} description={t('orderCutoffHint')}>
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="time"
                    defaultValue={settings.orderCutoff}
                    disabled={readOnly}
                  />
                )}
              </Field>

              <Field label={t('weightPrecision')} hint="g">
                {({ id }) => (
                  <Input
                    id={id}
                    numeric
                    defaultValue={settings.weightPrecisionG}
                    disabled={readOnly}
                    suffix="g"
                  />
                )}
              </Field>
            </div>
          </Section>

          <Section title={t('tax')}>
            <p className="pd-settings__note">
              <Code>{settings.taxScheme.id}</Code> · {settings.taxScheme.label}
            </p>
            <table className="pd-settings__rates">
              <thead>
                <tr>
                  <th className="stencil">{t('common:labels.none')}</th>
                  <th className="stencil right">{t('common:labels.tax')}</th>
                  <th className="stencil right">{t('common:labels.surcharge')}</th>
                </tr>
              </thead>
              <tbody>
                {settings.taxScheme.rates.map((rate) => (
                  <tr key={rate.key}>
                    <td>
                      <Code>{rate.key}</Code>
                      <span className="dim"> {rate.label}</span>
                      {rate.key === settings.taxScheme.defaultRateKey ? (
                        <Stamp tone="accent" size="sm">{t('common:labels.all')}</Stamp>
                      ) : null}
                    </td>
                    <td className="right num">{format.percentFromBps(rate.bps)}</td>
                    <td className="right num">
                      {rate.surchargeBps > 0 ? format.percentFromBps(rate.surchargeBps) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title={t('modules')}>
            <p className="pd-settings__note dim">{t('modulesHint')}</p>
            <div className="pd-settings__modules">
              {ModuleIdSchema.options.map((moduleId) => (
                <Checkbox
                  key={moduleId}
                  label={moduleId}
                  defaultChecked={business.enabledModules.includes(moduleId)}
                  disabled={readOnly || moduleId === 'dashboard' || moduleId === 'settings'}
                />
              ))}
            </div>
          </Section>
        </div>
      </ListPane>
    </Workbench>
  );
}
